/*
  Component: Horoscope
  Responsibility: opening a sign's reading in place, and filling it from the prediction service.

  WHAT IT DOES NOT DO is change the address. Pressing a sign hides the grid and shows the reading;
  the URL, the history and the scroll position are left alone. The price is that a reading cannot
  be linked to or reloaded into, and the note in snippets/kl-horoscope-sign.liquid says what
  changing that would take.

  THE SERVICE'S SHAPE, taken from the Hydrogen storefront that already calls it. Every one of these
  was a guess until then, and two of the guesses were wrong:

    method    POST, with Content-Type: application/json and an empty {} body. Not GET.
    daily     sun_sign_prediction/daily/{sign}
              sun_sign_prediction/daily/previous/{sign}
              sun_sign_prediction/daily/next/{sign}
    monthly   horoscope_prediction/monthly/{sign}   <- a DIFFERENT base path to the daily ones
    auth      HTTP Basic, userId:apiKey

  CREDENTIALS ARE A MERCHANT SETTING AND ARE VISIBLE TO VISITORS. There is no way around the second
  half of that: a storefront runs in the browser, so anything it can send, anyone can read out of
  the page. They are settings rather than constants so they can be rotated from the theme editor
  without a deploy, and so the same code works against a proxy — leave them blank, point the base
  URL at something that adds the header itself, and nothing here changes.

  It writes no markup. Every element it fills is already in the page from
  snippets/kl-horoscope-detail.liquid, and the one thing of an unknown length — the monthly
  reading's paragraphs — is cloned from a <template> that Liquid owns. Everything below is
  textContent, which is what keeps a third party's prose out of innerHTML.

  Responses are cached for the life of the page. A shopper comparing two signs, or switching
  between daily and monthly and back, should not spend the merchant's API quota twice for the same
  words.
*/

/*
  The three readings the service answers for, in the order they sit in time. The arrows move one
  place along this list and stop at its ends — see the note on updateDayButtons for why they step
  rather than toggle.
*/
const DAY_ORDER = ['previous', 'today', 'next'];
const TODAY_INDEX = 1;

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/*
  path -> parsed response. Keyed by the address that produced it, so a sign's daily and monthly
  readings, and each of its three days, are separate entries and none can be served for another.
*/
const readingCache = new Map();

/*
  Which sign is open, which reading is showing, and which day. Module scope because it outlives any
  one element: the panel is re-read on every press and the state is not.

  currentDayIndex is a position in DAY_ORDER rather than a name, because the arrows move by one and
  an index is what "by one" is expressible in. The service takes a name, so it is read back out at
  the point of the request and nowhere else.
*/
let currentSignKey = '';

/* The block whose reading is open, which is not the same as the sign it names — see the note in
   openSign. The key addresses the prediction service; the id addresses the settings. */
let currentSignId = '';
let currentMode = 'daily';
let currentDayIndex = TODAY_INDEX;

/*
  Pure and DOM-free: the address of one reading.

  Monthly is not a variation of daily — it is a different resource with its own base path, which is
  why this branches before it builds anything rather than swapping one segment. 'today' has no
  segment of its own, which is why it is not simply appended.
*/
function getReadingPath(baseUrl, signKey, mode, day) {
  if (mode === 'monthly') return `${baseUrl}/horoscope_prediction/monthly/${signKey}`;
  if (day === 'today') return `${baseUrl}/sun_sign_prediction/daily/${signKey}`;

  return `${baseUrl}/sun_sign_prediction/daily/${day}/${signKey}`;
}

/*
  Pure and DOM-free: the service's "21-8-2026" as the design's "Aug 21, 2026".

  Day and month are not padded in the response and the month is 1-based, hence the subtraction.
  Anything it does not recognise is handed back untouched rather than replaced with a guess: a date
  in an unexpected format is still more use to a reader than an empty line, and far more use than
  today's date wearing yesterday's label.
*/
function formatPredictionDate(rawDate) {
  if (typeof rawDate !== 'string') return '';

  const parts = rawDate.split('-');

  if (parts.length !== 3) return rawDate;

  const day = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);
  const monthName = MONTH_NAMES[month - 1];

  if (Number.isNaN(day) || !monthName) return rawDate;

  return `${monthName} ${day}, ${parts[2]}`;
}

/*
  Pure and DOM-free: the Authorization header value, or an empty string when no credentials are
  set. Empty is a supported state and not a misconfiguration — it is what pointing the base URL at
  a proxy looks like, because the proxy holds the credentials instead.
*/
function getAuthorizationHeader(userId, apiKey) {
  if (!userId || !apiKey) return '';

  try {
    return `Basic ${btoa(`${userId}:${apiKey}`)}`;
  } catch (error) {
    console.debug('Horoscope could not encode its credentials', { error });
    return '';
  }
}

function getRootElement() {
  return document.querySelector('[data-kl-horoscope]');
}

/*
  One request per address for the life of the page. The cache is checked before the fetch and
  written after it, so two presses on the same sign cost one request — and a failure is NOT cached,
  because a shopper pressing again after a dropped connection deserves another attempt.

  POST with an empty body is the service's own convention, not a mistake: it takes no parameters
  beyond the ones in the path.
*/
async function fetchReading(rootElement, signKey, mode, day) {
  const baseUrl = rootElement.dataset.klHoroscopeApi;
  const path = getReadingPath(baseUrl, signKey, mode, day);

  if (readingCache.has(path)) return readingCache.get(path);

  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const authorization = getAuthorizationHeader(
    rootElement.dataset.klHoroscopeUser,
    rootElement.dataset.klHoroscopeKey,
  );

  if (authorization) headers.Authorization = authorization;

  const response = await fetch(path, { method: 'POST', headers, body: '{}' });

  if (!response.ok) throw new Error(`Prediction service responded ${response.status}`);

  const reading = await response.json();

  readingCache.set(path, reading);

  return reading;
}

function setMessage(rootElement, messageText) {
  const messageElement = rootElement.querySelector('[data-kl-horoscope-message]');

  if (!messageElement) return;

  messageElement.hidden = !messageText;
  if (messageElement.textContent !== messageText) messageElement.textContent = messageText;
}

/*
  The six paragraphs of a daily reading. Each panel names the field it shows, so this walks the
  panels and asks the response for that key rather than knowing the six names itself — which keeps
  the service's shape in one place, the markup.

  A field the response does not carry empties its paragraph rather than leaving the previous sign's
  words under the new sign's heading.
*/
function renderDaily(rootElement, reading) {
  const prediction = reading.prediction || {};
  const dateElement = rootElement.querySelector('[data-kl-horoscope-date]');

  if (dateElement) dateElement.textContent = formatPredictionDate(reading.prediction_date);

  rootElement.querySelectorAll('[data-kl-horoscope-field]').forEach((fieldElement) => {
    const fieldText = prediction[fieldElement.dataset.klHoroscopeField] || '';

    if (fieldElement.textContent !== fieldText) fieldElement.textContent = fieldText;
  });
}

/*
  The monthly reading, which arrives as an array of an unknown length. The paragraph's shape is the
  <template> in snippets/kl-horoscope-detail.liquid; this clones it once per entry and sets text,
  so a reading containing angle brackets is words and not elements.

  Rebuilt rather than patched: a reading is a handful of paragraphs, and one code path that always
  produces the right ones beats several that each have to be right about what changed.
*/
function renderMonthly(rootElement, reading) {
  const monthElement = rootElement.querySelector('[data-kl-horoscope-month]');
  const bodyElement = rootElement.querySelector('[data-kl-horoscope-month-body]');
  const templateElement = rootElement.querySelector('[data-kl-horoscope-month-template]');

  if (monthElement) {
    const monthFormat = monthElement.dataset.klHoroscopeMonthFormat || '';

    monthElement.textContent = reading.prediction_month
      ? monthFormat.replace('[month]', reading.prediction_month)
      : '';
  }

  if (!bodyElement || !templateElement) return;

  const paragraphs = Array.isArray(reading.prediction) ? reading.prediction : [];

  bodyElement.replaceChildren();

  paragraphs.forEach((paragraphText) => {
    const paragraphElement = templateElement.content.firstElementChild.cloneNode(true);

    paragraphElement.textContent = paragraphText;
    bodyElement.appendChild(paragraphElement);
  });
}

/*
  Pure and DOM-free: which way an arrow moves. Named rather than inlined because two places ask the
  same question and they must not disagree — the one that decides whether a button is disabled, and
  the one that acts on the press.
*/
function getDayStep(dayElement) {
  return dayElement.dataset.klHoroscopeDay === 'next' ? 1 : -1;
}

/*
  EACH ARROW MOVES ONE DAY AND STOPS AT THE END. It used to toggle instead — each button flipped
  between its own day and today, and its label changed to "Today" when it was the day on screen.
  That is what the live storefront does and it is wrong: from yesterday, the button still reading
  "Next Day" jumped to tomorrow, skipping today entirely. A control labelled "Next Day" that
  advances two days is a bug however it is dressed.

  So the labels are fixed, they mean what they say, and an arrow with nowhere to go is disabled.
  Nothing is lost by dropping the "Today" shortcut: today is one press from either end, and a
  disabled arrow is also the honest way to say that three days is all the service holds.
*/
function updateDayButtons(rootElement) {
  rootElement.querySelectorAll('[data-kl-horoscope-day]').forEach((dayElement) => {
    dayElement.disabled = !DAY_ORDER[currentDayIndex + getDayStep(dayElement)];
  });
}

/*
  Fetch whatever the current sign, mode and day add up to, and draw it.

  Guarded on the base URL first, because with none set every request would fail and six paragraphs
  of "could not load" is a worse answer than one line saying the readings are not configured. That
  is a merchant's oversight, so it is logged with the context needed to fix it.
*/
async function loadCurrentReading(rootElement) {
  if (!rootElement.dataset.klHoroscopeApi) {
    setMessage(rootElement, rootElement.dataset.klHoroscopeErrorText || '');
    console.debug(
      'Horoscope: no prediction service URL is set, so no reading can be fetched. Set one in the section settings.',
      { signKey: currentSignKey },
    );
    return;
  }

  setMessage(rootElement, rootElement.dataset.klHoroscopeLoadingText || '');

  const requestedSignKey = currentSignKey;
  const requestedMode = currentMode;
  const requestedDay = DAY_ORDER[currentDayIndex];

  try {
    const reading = await fetchReading(rootElement, requestedSignKey, requestedMode, requestedDay);

    /*
      A slow answer for a sign the shopper has already navigated away from is dropped rather than
      drawn. Without this, pressing two signs quickly paints whichever request happened to finish
      last, which is not necessarily the one on screen.
    */
    if (
      requestedSignKey !== currentSignKey ||
      requestedMode !== currentMode ||
      requestedDay !== DAY_ORDER[currentDayIndex]
    ) {
      return;
    }

    setMessage(rootElement, '');

    if (requestedMode === 'monthly') {
      renderMonthly(rootElement, reading);
    } else {
      renderDaily(rootElement, reading);
    }
  } catch (error) {
    setMessage(rootElement, rootElement.dataset.klHoroscopeErrorText || '');
    console.debug('Horoscope could not load a reading', {
      signKey: requestedSignKey,
      mode: requestedMode,
      day: requestedDay,
      error,
    });
  }
}

function showMode(rootElement, mode) {
  const dailyElement = rootElement.querySelector('[data-kl-horoscope-daily]');
  const monthlyElement = rootElement.querySelector('[data-kl-horoscope-monthly]');

  currentMode = mode;

  /*
    Every switch back to daily starts at today. A shopper who read tomorrow's, went to the monthly
    and came back has not asked to still be on tomorrow, and a day they did not choose reads as a
    bug. The live storefront resets here too.
  */
  currentDayIndex = TODAY_INDEX;
  updateDayButtons(rootElement);

  if (dailyElement) dailyElement.hidden = mode !== 'daily';
  if (monthlyElement) monthlyElement.hidden = mode !== 'monthly';

  loadCurrentReading(rootElement);
}

/*
  Says which sign is open, and the collection and heading it carries, for anything on the page
  that follows the reading. assets/kl-horoscope-collection.js is the one listener today: it swaps
  the product row under this section. An event rather than a call, because that row is a section
  of its own and this file should not know whether it is on the page at all — an empty handle
  means "go back to the row the page shipped with".
*/
function announceSign(signKey, collectionHandle, headingText) {
  document.dispatchEvent(
    new CustomEvent('kl:horoscope:sign', {
      detail: { signKey, collectionHandle, headingText },
    }),
  );
}

function openSign(rootElement, signElement) {
  const headerElement = rootElement.querySelector('[data-kl-horoscope-header]');
  const signsElement = rootElement.querySelector('[data-kl-horoscope-signs]');
  const detailElement = rootElement.querySelector('[data-kl-horoscope-detail]');

  if (!signsElement || !detailElement || !signElement) return;

  /*
    The block that was pressed, not the sign it names. Those are different things and only the
    first is unique: two blocks may name the same sign, and while this worked from the sign both
    of their banners appeared at once.
  */
  const signId = signElement.dataset.klHoroscopeSignId || '';

  announceSign(
    signElement.dataset.klHoroscopeSign || '',
    signElement.dataset.klHoroscopeSignCollection || '',
    signElement.dataset.klHoroscopeSignHeading || '',
  );

  currentSignKey = signElement.dataset.klHoroscopeSign || '';
  currentSignId = signId;
  currentDayIndex = TODAY_INDEX;

  /*
    Only this block's banner is shown. The rest stay hidden and therefore unfetched — a lazy image
    inside a hidden element is never requested, which is what makes twelve banners in the markup
    cost twelve tags and one download.
  */
  rootElement.querySelectorAll('[data-kl-horoscope-banner]').forEach((bannerElement) => {
    bannerElement.hidden = bannerElement.dataset.klHoroscopeBanner !== signId;
  });

  if (headerElement) headerElement.hidden = true;
  signsElement.hidden = true;
  detailElement.hidden = false;

  updateDayButtons(rootElement);
  loadCurrentReading(rootElement);

  /*
    Focus moves to the reading, because a press that replaces what is on screen has to take the
    keyboard with it — otherwise the next Tab continues from a button that is no longer visible.
    tabindex is set here rather than in the markup so the element is not in the tab order itself;
    it is a destination, not a stop.
  */
  detailElement.tabIndex = -1;
  detailElement.focus();
}

function closeDetail(rootElement) {
  const headerElement = rootElement.querySelector('[data-kl-horoscope-header]');
  const signsElement = rootElement.querySelector('[data-kl-horoscope-signs]');
  const detailElement = rootElement.querySelector('[data-kl-horoscope-detail]');

  if (!signsElement || !detailElement) return;

  announceSign('', '', '');

  detailElement.hidden = true;
  signsElement.hidden = false;
  if (headerElement) headerElement.hidden = false;

  /*
    Focus returns to the sign that was opened, which is where it came from. Falling back to the
    grid itself rather than leaving it on a hidden element, because a merchant can delete a sign
    between renders in the theme editor.
  */
  const signElement = rootElement.querySelector(`[data-kl-horoscope-sign-id="${currentSignId}"]`);

  if (signElement) {
    signElement.focus();
  } else {
    signsElement.tabIndex = -1;
    signsElement.focus();
  }

  currentSignKey = '';
  currentSignId = '';
}

/*
  One click handler for the page, reading its target's hook. Delegated at the document rather than
  bound per element so the theme editor re-rendering the section cannot leave a listener behind or
  add a second one — the same conclusion assets/kl-cart-drawer.js and assets/kl-bundle-builder.js
  reached, and for the same reason.
*/
function handleDocumentClick(event) {
  if (!event.target || typeof event.target.closest !== 'function') return;

  const rootElement = getRootElement();

  if (!rootElement) return;

  const signElement = event.target.closest('[data-kl-horoscope-sign]');

  if (signElement) {
    openSign(rootElement, signElement);
    return;
  }

  if (event.target.closest('[data-kl-horoscope-back]')) {
    closeDetail(rootElement);
    return;
  }

  const dayElement = event.target.closest('[data-kl-horoscope-day]');

  if (dayElement) {
    const nextIndex = currentDayIndex + getDayStep(dayElement);

    /*
      Guarded as well as disabled. The attribute stops a press in the browser; this stops one that
      arrives anyway — and it is what keeps the index inside DAY_ORDER rather than trusting the
      button to have been drawn correctly.
    */
    if (!DAY_ORDER[nextIndex]) return;

    currentDayIndex = nextIndex;
    updateDayButtons(rootElement);
    loadCurrentReading(rootElement);
  }
}

/*
  The mode radios. A change event rather than a click, so the arrow keys move between them as a
  radio group should and the reading follows.
*/
function handleDocumentChange(event) {
  if (!event.target || typeof event.target.closest !== 'function') return;

  const modeElement = event.target.closest('[data-kl-horoscope-mode]');

  if (!modeElement) return;

  const rootElement = getRootElement();

  if (rootElement) showMode(rootElement, modeElement.dataset.klHoroscopeMode);
}

document.addEventListener('click', handleDocumentClick);
document.addEventListener('change', handleDocumentChange);

/*
  THEME EDITOR ONLY. Changing a sign's settings re-renders this section, which throws away the
  open reading and puts the grid back — so a merchant who has just chosen a collection for the
  sign they are looking at sees the row underneath still showing the last one, and has to press
  the sign a second time to find out whether their change worked.

  So the sign that was open is opened again. That re-reads the block's attributes from the fresh
  markup and announces them, which is what carries the new collection and heading to
  assets/kl-horoscope-collection.js. The reading itself is served from readingCache, so nothing
  is fetched twice.

  A sign that no longer exists — the block was deleted, or its name was cleared — leaves the grid
  showing and tells the row to go back to its own collection, rather than reopening something
  that is not there.
*/
document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.querySelector('[data-kl-horoscope]')) return;
  if (!currentSignId) return;

  const rootElement = getRootElement();

  if (!rootElement) return;

  const signElement = rootElement.querySelector(`[data-kl-horoscope-sign-id="${currentSignId}"]`);

  if (signElement) {
    openSign(rootElement, signElement);
    return;
  }

  currentSignKey = '';
  currentSignId = '';
  announceSign('', '', '');
});
